import type { Project } from "../../types/project";
import type { StoryMapSummary } from "../../types/storyMap";

export const SORT_KEYS = [
  "name",
  "folder",
  "updated",
  "owner",
  "strategies",
  "simulations",
  "curing",
] as const;
export type SortKey = (typeof SORT_KEYS)[number];
export type SortDir = "asc" | "desc";
export interface SortSpec {
  key: SortKey;
  dir: SortDir;
}

/** Stored in User.ui_flags as "<key>:<dir>"; anything else means "no sort". */
export function parseSort(raw: unknown): SortSpec | null {
  if (typeof raw !== "string") return null;
  const [key, dir] = raw.split(":");
  if (!(SORT_KEYS as readonly string[]).includes(key)) return null;
  return { key: key as SortKey, dir: dir === "desc" ? "desc" : "asc" };
}

export function serializeSort(sort: SortSpec | null): string | null {
  return sort ? `${sort.key}:${sort.dir}` : null;
}

/** Newest first is the natural first click on dates and counts; A-Z on text. */
function defaultDir(key: SortKey): SortDir {
  return key === "name" || key === "folder" || key === "owner" ? "asc" : "desc";
}

/** Header click cycle: default direction, the other direction, then off. */
export function nextSort(current: SortSpec | null, key: SortKey): SortSpec | null {
  if (!current || current.key !== key) return { key, dir: defaultDir(key) };
  if (current.dir === defaultDir(key)) {
    return { key, dir: current.dir === "asc" ? "desc" : "asc" };
  }
  return null;
}

type Value = string | number | null;

function valueOf(project: Project, key: SortKey, folderLabel: (p: Project) => string): Value {
  switch (key) {
    case "name":
      return project.name ?? "";
    case "folder":
      return folderLabel(project);
    case "owner":
      return project.username ?? "";
    case "updated": {
      const t = project.updated ? Date.parse(project.updated) : NaN;
      return Number.isFinite(t) ? t : null;
    }
    case "strategies":
      return project.strategy_count ?? null;
    case "simulations":
      return project.fire_simulation_count ?? null;
    case "curing":
      return project.curing_summary?.mean_pct ?? null;
  }
}

function compare(a: Value, b: Value, dir: SortDir): number {
  // Missing values sit at the bottom whichever way the column is sorted.
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  const raw =
    typeof a === "string" && typeof b === "string"
      ? a.localeCompare(b, undefined, { sensitivity: "base" })
      : Number(a) - Number(b);
  return dir === "asc" ? raw : -raw;
}

/**
 * Sort the table's project rows. Pinned projects always form the top block:
 * a pin is a deliberate tenant-wide ordering, so a column sort re-orders
 * within the pinned and unpinned blocks rather than across them. With no
 * sort the list keeps the order `useProjects` already provides.
 */
export function sortProjects(
  projects: Project[],
  sort: SortSpec | null,
  folderLabel: (p: Project) => string
): Project[] {
  if (!sort) return projects;
  const pinned = projects.filter((p) => !!p.is_pinned);
  const rest = projects.filter((p) => !p.is_pinned);
  const sortBlock = (block: Project[]) =>
    block
      .map((p, i) => ({ p, i }))
      .sort(
        (x, y) =>
          compare(valueOf(x.p, sort.key, folderLabel), valueOf(y.p, sort.key, folderLabel), sort.dir) ||
          x.i - y.i
      )
      .map(({ p }) => p);
  return [...sortBlock(pinned), ...sortBlock(rest)];
}

// ---------------------------------------------------------------------------
// Story-map browse mode
// ---------------------------------------------------------------------------

export const STORY_SORT_KEYS = ["title", "scope", "status", "scenes", "updated", "author"] as const;
export type StorySortKey = (typeof STORY_SORT_KEYS)[number];
export interface StorySortSpec {
  key: StorySortKey;
  dir: SortDir;
}

export function parseStorySort(raw: unknown): StorySortSpec | null {
  if (typeof raw !== "string") return null;
  const [key, dir] = raw.split(":");
  if (!(STORY_SORT_KEYS as readonly string[]).includes(key)) return null;
  return { key: key as StorySortKey, dir: dir === "desc" ? "desc" : "asc" };
}

export function serializeStorySort(sort: StorySortSpec | null): string | null {
  return sort ? `${sort.key}:${sort.dir}` : null;
}

function storyDefaultDir(key: StorySortKey): SortDir {
  return key === "scenes" || key === "updated" ? "desc" : "asc";
}

export function nextStorySort(current: StorySortSpec | null, key: StorySortKey): StorySortSpec | null {
  if (!current || current.key !== key) return { key, dir: storyDefaultDir(key) };
  if (current.dir === storyDefaultDir(key)) {
    return { key, dir: current.dir === "asc" ? "desc" : "asc" };
  }
  return null;
}

/** Draft, then failed, publishing, published to the organisation, public. */
export function storyStatusRank(story: StoryMapSummary): number {
  switch (story.status) {
    case "DRAFT":
      return 0;
    case "PUBLISH_FAILED":
      return 1;
    case "PUBLISHING":
      return 2;
    case "PUBLISHED":
      return story.visibility === "PUBLIC" ? 4 : 3;
  }
}

function storyValueOf(story: StoryMapSummary, key: StorySortKey, scopeLabel: (s: StoryMapSummary) => string): Value {
  switch (key) {
    case "title":
      return story.title ?? "";
    case "scope":
      return scopeLabel(story);
    case "status":
      return storyStatusRank(story);
    case "scenes":
      return story.scene_count ?? null;
    case "updated": {
      const t = story.updated_at ? Date.parse(story.updated_at) : NaN;
      return Number.isFinite(t) ? t : null;
    }
    case "author":
      return story.created_by_name ?? null;
  }
}

/** Sort the story rows; with no sort they keep the order given (newest first). */
export function sortStories(
  stories: StoryMapSummary[],
  sort: StorySortSpec | null,
  scopeLabel: (s: StoryMapSummary) => string
): StoryMapSummary[] {
  if (!sort) return stories;
  return stories
    .map((s, i) => ({ s, i }))
    .sort(
      (x, y) =>
        compare(storyValueOf(x.s, sort.key, scopeLabel), storyValueOf(y.s, sort.key, scopeLabel), sort.dir) ||
        x.i - y.i
    )
    .map(({ s }) => s);
}
